import { FC, useEffect, useRef, useState } from 'react';
import { cn } from '@crisellevaldez/speculo-ui';

interface TypewriterProps {
  /** Text(s) to type. Pass an array to cycle between phrases. */
  text: string | string[];
  /** Typing speed in ms per character. Default: 45 */
  speed?: number;
  /** Deleting speed in ms per character (when looping). Default: 25 */
  deleteSpeed?: number;
  /** Pause at the end of a phrase before deleting / advancing, in ms. Default: 1800 */
  pauseDuration?: number;
  /** Show a blinking cursor at the typing position. Default: true */
  showCursor?: boolean;
  /** Loop forever (only meaningful for arrays, or single text you want to retype). Default: array=true, single=false */
  loop?: boolean;
  /** Trigger key — change this to restart the animation (e.g. on modal open). */
  startKey?: string | number;
  className?: string;
}

/**
 * Typewriter — types out text character by character. Supports a single
 * phrase or an array that cycles. Used for Ask Remi greeting and other
 * "human typing" affordances.
 */
export const Typewriter: FC<TypewriterProps> = ({
  text,
  speed = 45,
  deleteSpeed = 25,
  pauseDuration = 1800,
  showCursor = true,
  loop,
  startKey,
  className,
}) => {
  const phrases = Array.isArray(text) ? text : [text];
  const shouldLoop = loop ?? phrases.length > 1;

  const [phraseIndex, setPhraseIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const timerRef = useRef<number | null>(null);

  // Restart on startKey change or when text/loop config changes
  useEffect(() => {
    setPhraseIndex(0);
    setCharIndex(0);
    setDeleting(false);
  }, [startKey, phrases.length, shouldLoop]);

  useEffect(() => {
    const current = phrases[phraseIndex] ?? '';

    // End of typing this phrase
    if (!deleting && charIndex === current.length) {
      // Single phrase, no loop → stop
      if (!shouldLoop && phrases.length === 1) return;
      // Pause then start deleting
      timerRef.current = window.setTimeout(() => setDeleting(true), pauseDuration);
      return () => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
      };
    }

    // Done deleting → advance to next phrase
    if (deleting && charIndex === 0) {
      setDeleting(false);
      setPhraseIndex((i) => (i + 1) % phrases.length);
      return;
    }

    // Step forward or backward by one character
    const delay = deleting ? deleteSpeed : speed;
    timerRef.current = window.setTimeout(
      () => setCharIndex((i) => i + (deleting ? -1 : 1)),
      delay
    );
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [charIndex, deleting, phraseIndex, phrases, speed, deleteSpeed, pauseDuration, shouldLoop]);

  const visible = (phrases[phraseIndex] ?? '').slice(0, charIndex);

  return (
    <span className={cn('inline-flex items-baseline', className)}>
      <span>{visible}</span>
      {showCursor && (
        <span
          aria-hidden
          className="ml-0.5 inline-block w-[2px] self-stretch bg-current animate-typewriter-cursor"
        />
      )}
    </span>
  );
};

export default Typewriter;
